import { ModalScreen } from "../modal-screen";
import { customElement, property, state } from "lit/decorators.js";
import { html } from "lit";
import { CustomToast } from "../toast";

@customElement("profile-settings")
export class ProfileSettings extends ModalScreen {
  @property({ type: String }) errors: any = {};
  @property({ type: Array }) tabs: any = [];
  @property({ type: String }) activeTab: string = "";
  @state() activeSubTab: string = "general";
  @property({ type: String }) email: string = "";
  @property({ type: String }) currentPassword: string = "";
  @property({ type: String }) newPassword: string = "";
  @property({ type: String }) confirmPassword: string = "";
  roomId: string;
  @property({ type: String }) message: string;

  // New state for verification code visibility
  @state() isVerificationVisible: boolean = false;
  @state() verificationCode: string = "";
  emailDisabled: boolean = true;

  constructor() {
    super();
  }

  async connectedCallback() {
    super.connectedCallback();
  }

  async openModal() {
    this.isOpen = true;
    this.tabs = [{ slug: "account", label: "Account" }];
    this.selectTab("account");

    try {
      const response = await fetch(
        `${process.env.BASE_API_URL}/profile-settings`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.email = data.email || "";
      } else {
        console.error("Failed to fetch room details");
      }
    } catch (error) {
      console.error("Error fetching room data:", error);
    }
  }

  selectTab(tabId: string) {
    this.activeTab = tabId;
  }

  selectSubTab(subTabId: string) {
    this.activeSubTab = subTabId;
  }

  handleInputChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.email = target.value;
  }

  // New method to handle verifying email
  async handleVerifyEmailClick() {
    this.isVerificationVisible = true;
    this.emailDisabled = false;

    const response = await fetch(`${process.env.BASE_API_URL}/verify-email`, {
      method: "POST",
      body: JSON.stringify({
        email: this.email,
        action: "verify-email",
      }),
      credentials: "include",
    });

    const data = await response.json();
    if (data.success) {
      const toast = document.querySelector("custom-toast") as CustomToast;
      toast?.openToast(
        "Verification email sent. Please check your inbox.",
        "top-right",
        3000,
        "success"
      );
    } else {
      const toast = document.querySelector("custom-toast") as CustomToast;
      toast?.openToast(data.error, "top-right", 3000, "error");
    }
  }

  // Method to handle input of the verification code
  handleVerificationCodeChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.verificationCode = target.value;
  }

  // Method to handle submitting the verification code
  async handleVerifyCodeSubmit(e: Event) {
    e.preventDefault();
    // Handle the API call to verify the code
    const response = await fetch(`${process.env.BASE_API_URL}/verify-email`, {
      method: "POST",
      body: JSON.stringify({
        email: this.email,
        code: this.verificationCode,
        action: "save-email",
      }),
      credentials: "include",
    });

    const data = await response.json();
    if (data.success) {
      const toast = document.querySelector("custom-toast") as CustomToast;
      toast?.openToast(
        "Email succesfully changed",
        "top-right",
        3000,
        "success"
      );
      this.isVerificationVisible = false;
    } else {
      const toast = document.querySelector("custom-toast") as CustomToast;

      if (data.error && !data.error.field) {
        toast?.openToast(
          data.error || "Verification failed",
          "top-right",
          3000,
          "error"
        );
      }

      if (data.error && data.error.field) {
        this.errors = data;
      }
    }
  }

  async onPasswordChange(e: Event) {
    const response = await fetch(
      `${process.env.BASE_API_URL}/profile-settings`,
      {
        method: "POST",
        body: JSON.stringify({
          currentPassword: this.currentPassword,
          newPassword: this.newPassword,
          confirmPassword: this.confirmPassword,
        }),
        credentials: "include",
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.error) {
        this.errors = data;
      } else {
        const toast = document.querySelector("custom-toast") as CustomToast;
        toast?.openToast(data.message, "top-right", 3000, "success");

        this.errors = {};
        this.currentPassword = "";
        this.confirmPassword = "";
        this.newPassword = "";
      }
    }
  }

  handlePasswordChange(e: Event) {
    const target = e.target as HTMLInputElement;
    if (target.id === "currentPassword") {
      this.currentPassword = target.value;
    } else if (target.id === "newPassword") {
      this.newPassword = target.value;
    } else if (target.id === "confirmPassword") {
      this.confirmPassword = target.value;
    }
  }

  async handleFormSubmit(e: Event) {
    e.preventDefault();
  }

  render() {
    if (!this.isOpen) {
      return html``;
    }

    return html`
      <div class="modal">
        <div class="modal__top">
          ${this.tabs.map(
            (tab) => html`
              <span
                class="modal__tab ${this.activeTab === tab.slug
                  ? "is-active"
                  : ""}"
                @click="${() => this.selectTab(tab.slug)}"
              >
                ${tab.label}
              </span>
            `
          )}
          <button class="modal__close" @click="${this.closeModal}">
            <span>Close</span>
          </button>
        </div>

        <div class="modal__body">
          ${this.activeTab === "account"
            ? html`
                <div class="sub-tabs">
                  <span
                    class="btn ${this.activeSubTab === "general"
                      ? "is-active"
                      : ""}"
                    @click="${() => this.selectSubTab("general")}"
                    >General</span
                  >
                  <span
                    class="btn ${this.activeSubTab === "password"
                      ? "is-active"
                      : ""}"
                    @click="${() => this.selectSubTab("password")}"
                    >Password</span
                  >
                </div>

                <form class="memberform" @submit="${this.handleFormSubmit}">
                  ${this.activeSubTab === "general"
                    ? html`
                        <p class="instruction-text">
                          To update your email address, you first need to verify
                          your current email. Click the "Verify email address"
                          button below to receive a verification link. Once your
                          email is verified, you will be able to enter a new
                          email address and save the changes.
                        </p>

                        <div class="memberform-input-group">
                          <label for="email">Email:</label>
                          <div style="display: flex; gap: 5px;">
                            <input
                              class="input ${this.emailDisabled
                                ? "is-disabled"
                                : ""}"
                              id="email"
                              type="text"
                              ${this.emailDisabled ? `disabled="disabled"` : ""}
                              .value="${this.email}"
                              @input="${this.handleInputChange}"
                            />
                            ${this.errors.field === "email"
                              ? html`<div class="error-message">
                                  ${this.errors.error}
                                </div>`
                              : ""}
                            <button
                              class="btn"
                              @click="${this.handleVerifyEmailClick}"
                              type="button"
                            >
                              Verify email address
                            </button>
                          </div>
                        </div>

                        <!-- Show verification input if verification is in progress -->
                        ${this.isVerificationVisible
                          ? html`
                              <div class="memberform-input-group">
                                <label for="verificationCode"
                                  >Verification Code:</label
                                >
                                <input
                                  class="input"
                                  id="verificationCode"
                                  type="text"
                                  .value="${this.verificationCode}"
                                  @input="${this.handleVerificationCodeChange}"
                                />
                              </div>
                            `
                          : ""}

                        <button
                          class="btn"
                          @click="${this.handleVerifyCodeSubmit}"
                          type="button"
                        >
                          Save
                        </button>
                      `
                    : html`
                        <p class="instruction-text">
                          Enter your current password and a new password.
                        </p>

                        <div class="memberform-input-group">
                          <label for="currentPassword">Current Password:</label>
                          <input
                            class="input"
                            id="currentPassword"
                            type="password"
                            .value="${this.currentPassword}"
                            @input="${this.handlePasswordChange}"
                          />
                          ${this.errors.field === "password"
                            ? html`<div class="error-message">
                                ${this.errors.error}
                              </div>`
                            : ""}
                        </div>
                        <div class="memberform-input-group">
                          <label for="newPassword">New Password:</label>
                          <input
                            class="input"
                            id="newPassword"
                            type="password"
                            .value="${this.newPassword}"
                            @input="${this.handlePasswordChange}"
                          />

                          ${this.errors.field === "newPassword"
                            ? html`<div class="error-message">
                                ${this.errors.error}
                              </div>`
                            : ""}
                        </div>
                        <div class="memberform-input-group">
                          <label for="confirmPassword">Confirm Password:</label>
                          <input
                            class="input"
                            id="confirmPassword"
                            type="password"
                            .value="${this.confirmPassword}"
                            @input="${this.handlePasswordChange}"
                          />

                          ${this.errors.field === "confirmPassword"
                            ? html`<div class="error-message">
                                ${this.errors.error}
                              </div>`
                            : ""}
                        </div>

                        <button
                          type="submit"
                          class="btn"
                          @click="${this.onPasswordChange}"
                        >
                          Save
                        </button>
                      `}
                </form>
              `
            : ""}
        </div>
      </div>
    `;
  }
}
